import get from 'lodash/get';
import { I18n } from 'react-redux-i18n';
import { MarketOption } from 'interface/index';
import { components } from 'apiTyped/types';

export const dataFilter = (data: any[] = [], filters: any[] = []) => {
  let filteredData = [...data];
  filters.forEach((filter: any) => {
    filteredData = filter.value
      ? filteredData.filter((item) => item[filter.key] === filter.value)
      : filter.default
        ? filter.default
        : filteredData;
  });
  return filteredData;
};

export const getMarketOptions = (
  data: components['schemas']['DataDetail'][] = []
): MarketOption[] => {
  const options: MarketOption[] = [
    ...new Set(data.map((item) => item.market_name))
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((market: string) => ({
      label: market,
      value: market,
      currency: get(
        data.find((wine) => wine.market_name === market),
        'local_currency',
        'EUR'
      )
    }));

  options.unshift(
    ...[
      {
        label: I18n.t('euroCountries'),
        value: 'EUR',
        currency: 'EUR',
        custom: true
      }
    ]
  );
  return options;
};

export const getLabels = (
  data: any[] = [],
  activeRadioButton: string | null,
  showVintage: boolean = false
): string[] => {
  const compateWithYearBefore =
    activeRadioButton === "Comparer avec l'année n-1";

  // Force hidding vintage if we compare with year before
  if (compateWithYearBefore) {
    showVintage = false;
  }

  // Extract wine and vintage
  let newData = data.map((set) =>
    set.map((item: any) => ({ wine: item.wine_name, vintage: item.vintage }))
  );

  // Flatten array
  // eslint-disable-next-line prefer-spread
  newData = [].concat.apply([], newData);

  // Remove undefined values
  newData = newData.filter((item) => item.wine && item.vintage);

  // Remove duplicates
  newData = newData.filter(
    (elem, index) =>
      index ===
      newData.findIndex(
        (elem2) => elem.wine === elem2.wine && elem.vintage === elem2.vintage
      )
  );

  // Construct labels
  const labels = newData.map((item) =>
    showVintage ? `${item.vintage} - ${item.wine}` : item.wine
  );

  if (compateWithYearBefore) {
    labels.push(labels[0] + ' - 1');
  }

  return [...new Set(labels)];
};

export const averageDataFacing = (data: any[], keyData: string): any => {
  const averagedData: any = {};
  data.forEach((item) => {
    if (averagedData[item[keyData]]) {
      averagedData[item[keyData]].sumFacing += item.facing;
      if (item.min_price < averagedData[item[keyData]].min_price) {
        averagedData[item[keyData]].min_store = item.min_store;
        averagedData[item[keyData]].min_price = item.min_price;
        averagedData[item[keyData]].min_local_price = item.min_local_price;
      }
      if (item.max_price > averagedData[item[keyData]].max_price) {
        averagedData[item[keyData]].max_store = item.max_store;
        averagedData[item[keyData]].max_price = item.max_price;
        averagedData[item[keyData]].max_local_price = item.max_local_price;
      }
      Object.keys(averagedData[item[keyData]]).forEach((key) => {
        if (key.includes('avg')) {
          averagedData[item[keyData]][key] =
            (averagedData[item[keyData]][key] *
              (averagedData[item[keyData]].sumFacing - item.facing) +
              item[key] * item.facing) /
            averagedData[item[keyData]].sumFacing;
        }
      });
    } else {
      averagedData[item[keyData]] = {
        ...item,
        sumFacing: item.facing
      };
    }
  });

  return Object.values(averagedData);
};

export const roundValue = (
  value: number = 0,
  precision: number = 10
): number => {
  if (Number.isFinite(value)) {
    const around = Number(value) % Number(precision);
    return Number(value - around);
  }
  return 0;
};

export const roundDecimal = (number: number, precision: number = 2): number => {
  const tmp = Math.pow(10, precision);
  return Math.round(number * tmp) / tmp;
};

export const filterByMarket = (
  data: components['schemas']['DataDetail'][],
  market: MarketOption | null,
  keyData: string
): components['schemas']['DataDetail'][] => {
  if (market) {
    data = data.filter((item) =>
      market.custom
        ? item.local_currency === market.currency
        : item.market_name === market.value
    );
  }
  return averageDataFacing(data, keyData);
};

export const getNameNetwork = (network: string) => {
  const names = {
    ON: I18n.t('prices.networkNames.on'),
    OFF: I18n.t('prices.networkNames.off'),
    WEB: I18n.t('prices.networkNames.web'),
    AUC: I18n.t('prices.networkNames.auc'),
    PRI: I18n.t('prices.networkNames.pri')
  };
  // @ts-expect-error, type
  return names[network];
};
